import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  MenuItem,
  Paper,
  Tab,
  Tabs,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import RefreshIcon from '@mui/icons-material/Refresh';
import {
  getLeagueCycle,
  getLeagueProfile,
  getLeagueSummary,
  getMindCricketApiPayload,
  getMindgameMatch,
  getMindgameSummary,
  isMindCricketApiSuccess,
  listLeagueCycles,
  listLeagueEvents,
  listLeagueMatches,
  listLeagueProfiles,
  listMindgameMatches,
  listMindgameUsers,
} from '../../services/mindCricketLeagueApi';
import {
  CYCLE_OUTCOMES,
  LEAGUE_EVENT_TYPES,
  MATCH_SCOPES,
  PAGE_SIZE,
  addDaysToDateString,
  formatDateTime,
  formatNullable,
  getLocalDateString,
} from './mindCricketLeagueUtils';

function KpiCard({ label, value, color = '#1A5276' }) {
  return (
    <Paper
      elevation={2}
      sx={{
        p: 2,
        minWidth: 140,
        flex: '1 1 140px',
        borderTop: `3px solid ${color}`,
      }}
    >
      <Typography variant="caption" color="text.secondary">
        {label}
      </Typography>
      <Typography variant="h5" sx={{ mt: 0.5 }}>
        {value ?? '—'}
      </Typography>
    </Paper>
  );
}

function PaginationBar({ offset, total, rowCount, onPrev, onNext, loading }) {
  const hasPrev = offset > 0;
  const hasMore = offset + rowCount < total;
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: 1.5 }}>
      <Typography variant="body2" color="text.secondary">
        Showing {rowCount === 0 ? 0 : offset + 1}–{offset + rowCount} of {total}
      </Typography>
      <Box sx={{ display: 'flex', gap: 1 }}>
        <Button size="small" variant="outlined" disabled={!hasPrev || loading} onClick={onPrev}>
          Previous
        </Button>
        <Button size="small" variant="outlined" disabled={!hasMore || loading} onClick={onNext}>
          Next
        </Button>
      </Box>
    </Box>
  );
}

function LevelHistogram({ data }) {
  if (!Array.isArray(data) || data.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary">
        No level histogram data.
      </Typography>
    );
  }
  const max = Math.max(...data.map((d) => Number(d.players) || 0), 1);
  return (
    <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: 0.75, height: 120, mt: 1 }}>
      {data.map((d) => {
        const players = Number(d.players) || 0;
        const height = Math.max(4, Math.round((players / max) * 100));
        return (
          <Box key={d.level} sx={{ flex: 1, textAlign: 'center' }}>
            <Typography variant="caption">{players}</Typography>
            <Box
              sx={{
                height,
                bgcolor: '#2471A3',
                borderRadius: '4px 4px 0 0',
                mx: 'auto',
                maxWidth: 28,
              }}
            />
            <Typography variant="caption" color="text.secondary">
              L{d.level}
            </Typography>
          </Box>
        );
      })}
    </Box>
  );
}

function JsonPreview({ data }) {
  return (
    <Box
      component="pre"
      sx={{
        m: 0,
        p: 2,
        bgcolor: '#f5f5f5',
        borderRadius: 1,
        overflow: 'auto',
        maxHeight: 480,
        fontSize: 12,
      }}
    >
      {JSON.stringify(data, null, 2)}
    </Box>
  );
}

function OverviewSection({ fromDate, toDate, onSessionExpired }) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [mind, setMind] = useState(null);
  const [league, setLeague] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [mindRes, leagueRes] = await Promise.all([
        getMindgameSummary({ from: fromDate || undefined, to: toDate || undefined }),
        getLeagueSummary({ from: fromDate || undefined, to: toDate || undefined }),
      ]);
      if (onSessionExpired(mindRes) || onSessionExpired(leagueRes)) return;
      if (!isMindCricketApiSuccess(mindRes)) {
        setError(mindRes?.message || 'Failed to load Mind Cricket summary.');
      } else {
        setMind(getMindCricketApiPayload(mindRes));
      }
      if (!isMindCricketApiSuccess(leagueRes)) {
        setError((prev) => prev || leagueRes?.message || 'Failed to load league summary.');
      } else {
        setLeague(getMindCricketApiPayload(leagueRes));
      }
    } catch (err) {
      setError(err.message || 'Failed to load summaries.');
    } finally {
      setLoading(false);
    }
  }, [fromDate, toDate, onSessionExpired]);

  useEffect(() => {
    load();
  }, [load]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  const outcomes = league?.cycle_outcomes || {};

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      {error && <Alert severity="error">{error}</Alert>}

      <Box>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
          <Typography variant="h6">Mind Cricket overview</Typography>
          <Button size="small" startIcon={<RefreshIcon />} onClick={load}>
            Refresh
          </Button>
        </Box>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5 }}>
          <KpiCard label="Total matches" value={mind?.total_matches} />
          <KpiCard label="PvP" value={mind?.pvp_matches} color="#C0392B" />
          <KpiCard label="PvAI" value={mind?.pvai_matches} color="#16A085" />
          <KpiCard label="League matches" value={mind?.league_matches} color="#8E44AD" />
          <KpiCard label="Completed" value={mind?.completed_matches} color="#27AE60" />
          <KpiCard label="Unique users" value={mind?.unique_users} color="#D35400" />
          <KpiCard label="Unique guests" value={mind?.unique_guests} color="#7F8C8D" />
          <KpiCard label="Matches 7d" value={mind?.matches_7d} color="#2980B9" />
          <KpiCard label="Matches 30d" value={mind?.matches_30d} color="#1ABC9C" />
        </Box>
      </Box>

      <Divider />

      <Box>
        <Typography variant="h6" sx={{ mb: 1 }}>
          Super Over League overview
        </Typography>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, mb: 2 }}>
          <KpiCard label="Profiles" value={league?.profiles} />
          <KpiCard label="Open cycles" value={league?.open_cycles} color="#F39C12" />
          <KpiCard label="Completed cycles" value={league?.completed_cycles} color="#27AE60" />
          <KpiCard label="Trophies" value={league?.trophies} color="#F1C40F" />
          <KpiCard label="League matches" value={league?.matches_total} color="#8E44AD" />
          <KpiCard label="Match wins" value={league?.match_wins} color="#16A085" />
          <KpiCard label="Stuck players" value={league?.stuck_players} color="#E74C3C" />
          <KpiCard label="Matches 7d" value={league?.matches_7d} color="#2980B9" />
          <KpiCard label="Matches 30d" value={league?.matches_30d} color="#1ABC9C" />
        </Box>

        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
          <Paper sx={{ p: 2, flex: '1 1 320px' }}>
            <Typography variant="subtitle2" gutterBottom>
              Level funnel
            </Typography>
            <LevelHistogram data={league?.level_histogram} />
          </Paper>
          <Paper sx={{ p: 2, flex: '1 1 240px' }}>
            <Typography variant="subtitle2" gutterBottom>
              Cycle outcomes
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1 }}>
              <Chip label={`Demotion: ${outcomes.demotion ?? 0}`} color="error" variant="outlined" />
              <Chip label={`Stay: ${outcomes.stay ?? 0}`} variant="outlined" />
              <Chip label={`Promotion: ${outcomes.promotion ?? 0}`} color="success" variant="outlined" />
              <Chip label={`Trophy: ${outcomes.trophy ?? 0}`} color="warning" variant="outlined" />
            </Box>
          </Paper>
        </Box>
      </Box>
    </Box>
  );
}

function MatchesSection({ fromDate, toDate, onSessionExpired, showSnackbar }) {
  const [scope, setScope] = useState('all');
  const [mode, setMode] = useState('');
  const [status, setStatus] = useState('');
  const [userId, setUserId] = useState('');
  const [offset, setOffset] = useState(0);
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await listMindgameMatches({
        scope: scope || undefined,
        mode: mode || undefined,
        status: status || undefined,
        user_id: userId || undefined,
        from: fromDate || undefined,
        to: toDate || undefined,
        limit: PAGE_SIZE,
        offset,
      });
      if (onSessionExpired(res)) return;
      if (!isMindCricketApiSuccess(res)) {
        setError(res?.message || 'Failed to load matches.');
        setRows([]);
        setTotal(0);
        return;
      }
      const data = getMindCricketApiPayload(res) || {};
      setRows(data.rows || []);
      setTotal(data.total ?? 0);
    } catch (err) {
      setError(err.message || 'Failed to load matches.');
      setRows([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [fromDate, toDate, mode, offset, onSessionExpired, scope, status, userId]);

  useEffect(() => {
    load();
  }, [load]);

  const openDetail = async (matchId) => {
    setDetailLoading(true);
    setDetail(null);
    try {
      const res = await getMindgameMatch(matchId);
      if (onSessionExpired(res)) return;
      if (!isMindCricketApiSuccess(res)) {
        showSnackbar(res?.message || 'Failed to load match detail.', 'error');
        return;
      }
      setDetail(getMindCricketApiPayload(res));
    } catch (err) {
      showSnackbar(err.message || 'Failed to load match detail.', 'error');
    } finally {
      setDetailLoading(false);
    }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, mb: 2 }}>
        <TextField
          select
          size="small"
          label="Scope"
          value={scope}
          onChange={(e) => {
            setScope(e.target.value);
            setOffset(0);
          }}
          sx={{ minWidth: 120 }}
        >
          {MATCH_SCOPES.map((s) => (
            <MenuItem key={s.value} value={s.value}>
              {s.label}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          select
          size="small"
          label="Mode"
          value={mode}
          onChange={(e) => {
            setMode(e.target.value);
            setOffset(0);
          }}
          sx={{ minWidth: 120 }}
        >
          <MenuItem value="">Any</MenuItem>
          <MenuItem value="PVP">PVP</MenuItem>
          <MenuItem value="PVAI">PVAI</MenuItem>
        </TextField>
        <TextField
          size="small"
          label="Status"
          value={status}
          onChange={(e) => {
            setStatus(e.target.value);
            setOffset(0);
          }}
          placeholder="MATCH_ENDED"
          sx={{ minWidth: 160 }}
        />
        <TextField
          size="small"
          label="User ID"
          value={userId}
          onChange={(e) => {
            setUserId(e.target.value);
            setOffset(0);
          }}
          sx={{ minWidth: 120 }}
        />
        <Button startIcon={<RefreshIcon />} onClick={load} disabled={loading}>
          Refresh
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 1 }}>
          {error}
        </Alert>
      )}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Match</TableCell>
                  <TableCell>Mode</TableCell>
                  <TableCell>League</TableCell>
                  <TableCell>Players</TableCell>
                  <TableCell>Scores</TableCell>
                  <TableCell>League won</TableCell>
                  <TableCell>Cycle</TableCell>
                  <TableCell>Created</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} align="center">
                      No matches found.
                    </TableCell>
                  </TableRow>
                ) : (
                  rows.map((row) => (
                    <TableRow
                      key={row.match_id}
                      hover
                      sx={{ cursor: 'pointer' }}
                      onClick={() => openDetail(row.match_id)}
                    >
                      <TableCell>{row.match_id}</TableCell>
                      <TableCell>{row.mode}</TableCell>
                      <TableCell>{row.is_league ? 'Yes' : '—'}</TableCell>
                      <TableCell>
                        {row.username_1 || row.user_id_1 || '—'}
                        {row.username_2 || row.user_id_2
                          ? ` vs ${row.username_2 || row.user_id_2}`
                          : ''}
                      </TableCell>
                      <TableCell>
                        {formatNullable(row.score_1 ?? row.p1_score)} /{' '}
                        {formatNullable(row.score_2 ?? row.p2_score)}
                      </TableCell>
                      <TableCell>
                        {row.league_won == null ? '—' : row.league_won ? 'Won' : 'Lost'}
                      </TableCell>
                      <TableCell>{formatNullable(row.league_cycle_id)}</TableCell>
                      <TableCell>{formatDateTime(row.created_at)}</TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <PaginationBar
            offset={offset}
            total={total}
            rowCount={rows.length}
            loading={loading}
            onPrev={() => setOffset((o) => Math.max(0, o - PAGE_SIZE))}
            onNext={() => setOffset((o) => o + PAGE_SIZE)}
          />
        </>
      )}

      <Dialog open={detailLoading || !!detail} onClose={() => setDetail(null)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          Match detail
          <IconButton onClick={() => setDetail(null)} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {detailLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
            <JsonPreview data={detail} />
          )}
        </DialogContent>
      </Dialog>
    </Box>
  );
}

function UsersSection({ fromDate, toDate, onSessionExpired }) {
  const [scope, setScope] = useState('all');
  const [q, setQ] = useState('');
  const [search, setSearch] = useState('');
  const [offset, setOffset] = useState(0);
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await listMindgameUsers({
        q: search || undefined,
        scope: scope || undefined,
        from: fromDate || undefined,
        to: toDate || undefined,
        limit: PAGE_SIZE,
        offset,
      });
      if (onSessionExpired(res)) return;
      if (!isMindCricketApiSuccess(res)) {
        setError(res?.message || 'Failed to load users.');
        setRows([]);
        setTotal(0);
        return;
      }
      const data = getMindCricketApiPayload(res) || {};
      setRows(data.rows || []);
      setTotal(data.total ?? 0);
    } catch (err) {
      setError(err.message || 'Failed to load users.');
      setRows([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [fromDate, toDate, offset, onSessionExpired, scope, search]);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <Box>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, mb: 2 }}>
        <TextField
          size="small"
          label="Search"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              setSearch(q.trim());
              setOffset(0);
            }
          }}
          placeholder="User id / username / guest"
          sx={{ minWidth: 220 }}
        />
        <TextField
          select
          size="small"
          label="Scope"
          value={scope}
          onChange={(e) => {
            setScope(e.target.value);
            setOffset(0);
          }}
          sx={{ minWidth: 120 }}
        >
          {MATCH_SCOPES.map((s) => (
            <MenuItem key={s.value} value={s.value}>
              {s.label}
            </MenuItem>
          ))}
        </TextField>
        <Button
          variant="outlined"
          onClick={() => {
            setSearch(q.trim());
            setOffset(0);
          }}
        >
          Search
        </Button>
        <Button startIcon={<RefreshIcon />} onClick={load} disabled={loading}>
          Refresh
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 1 }}>
          {error}
        </Alert>
      )}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>User</TableCell>
                  <TableCell>Guest</TableCell>
                  <TableCell>Games</TableCell>
                  <TableCell>W / L / T</TableCell>
                  <TableCell>League games</TableCell>
                  <TableCell>League wins</TableCell>
                  <TableCell>Level</TableCell>
                  <TableCell>Highest</TableCell>
                  <TableCell>Trophies</TableCell>
                  <TableCell>Last played</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={10} align="center">
                      No users found.
                    </TableCell>
                  </TableRow>
                ) : (
                  rows.map((row, idx) => (
                    <TableRow key={row.user_id ?? row.guest_hash_prefix ?? idx}>
                      <TableCell>
                        {row.username || '—'}
                        {row.user_id != null ? ` (#${row.user_id})` : ''}
                      </TableCell>
                      <TableCell>{formatNullable(row.guest_hash_prefix)}</TableCell>
                      <TableCell>{row.games_played ?? 0}</TableCell>
                      <TableCell>
                        {row.wins ?? 0} / {row.losses ?? 0} / {row.ties ?? 0}
                      </TableCell>
                      <TableCell>{row.league_games ?? 0}</TableCell>
                      <TableCell>{row.league_wins ?? 0}</TableCell>
                      <TableCell>{formatNullable(row.current_level)}</TableCell>
                      <TableCell>{formatNullable(row.highest_level)}</TableCell>
                      <TableCell>{row.total_trophies ?? 0}</TableCell>
                      <TableCell>{formatDateTime(row.last_played_at)}</TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <PaginationBar
            offset={offset}
            total={total}
            rowCount={rows.length}
            loading={loading}
            onPrev={() => setOffset((o) => Math.max(0, o - PAGE_SIZE))}
            onNext={() => setOffset((o) => o + PAGE_SIZE)}
          />
        </>
      )}
    </Box>
  );
}

function LeagueDeepDive({ fromDate, toDate, onSessionExpired, showSnackbar }) {
  const [subTab, setSubTab] = useState(0);

  return (
    <Box>
      <Tabs value={subTab} onChange={(_, v) => setSubTab(v)} sx={{ mb: 2 }}>
        <Tab label="Profiles" />
        <Tab label="Cycles" />
        <Tab label="League matches" />
        <Tab label="Events" />
      </Tabs>
      {subTab === 0 && (
        <ProfilesPanel
          fromDate={fromDate}
          toDate={toDate}
          onSessionExpired={onSessionExpired}
          showSnackbar={showSnackbar}
        />
      )}
      {subTab === 1 && (
        <CyclesPanel
          fromDate={fromDate}
          toDate={toDate}
          onSessionExpired={onSessionExpired}
          showSnackbar={showSnackbar}
        />
      )}
      {subTab === 2 && (
        <LeagueMatchesPanel fromDate={fromDate} toDate={toDate} onSessionExpired={onSessionExpired} />
      )}
      {subTab === 3 && (
        <EventsPanel fromDate={fromDate} toDate={toDate} onSessionExpired={onSessionExpired} />
      )}
    </Box>
  );
}

function ProfilesPanel({ fromDate, toDate, onSessionExpired, showSnackbar }) {
  const [level, setLevel] = useState('');
  const [userId, setUserId] = useState('');
  const [minTrophies, setMinTrophies] = useState('');
  const [offset, setOffset] = useState(0);
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await listLeagueProfiles({
        level: level || undefined,
        user_id: userId || undefined,
        min_trophies: minTrophies || undefined,
        from: fromDate || undefined,
        to: toDate || undefined,
        limit: PAGE_SIZE,
        offset,
      });
      if (onSessionExpired(res)) return;
      if (!isMindCricketApiSuccess(res)) {
        setError(res?.message || 'Failed to load profiles.');
        setRows([]);
        setTotal(0);
        return;
      }
      const data = getMindCricketApiPayload(res) || {};
      setRows(data.rows || []);
      setTotal(data.total ?? 0);
    } catch (err) {
      setError(err.message || 'Failed to load profiles.');
    } finally {
      setLoading(false);
    }
  }, [fromDate, toDate, level, minTrophies, offset, onSessionExpired, userId]);

  useEffect(() => {
    load();
  }, [load]);

  const openDetail = async (id) => {
    setDetailLoading(true);
    setDetail(null);
    try {
      const res = await getLeagueProfile(id);
      if (onSessionExpired(res)) return;
      if (!isMindCricketApiSuccess(res)) {
        showSnackbar(res?.message || 'Failed to load profile.', 'error');
        return;
      }
      setDetail(getMindCricketApiPayload(res));
    } catch (err) {
      showSnackbar(err.message || 'Failed to load profile.', 'error');
    } finally {
      setDetailLoading(false);
    }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, mb: 2 }}>
        <TextField
          size="small"
          label="Level"
          type="number"
          value={level}
          onChange={(e) => {
            setLevel(e.target.value);
            setOffset(0);
          }}
          sx={{ width: 100 }}
        />
        <TextField
          size="small"
          label="User ID"
          value={userId}
          onChange={(e) => {
            setUserId(e.target.value);
            setOffset(0);
          }}
          sx={{ width: 120 }}
        />
        <TextField
          size="small"
          label="Min trophies"
          type="number"
          value={minTrophies}
          onChange={(e) => {
            setMinTrophies(e.target.value);
            setOffset(0);
          }}
          sx={{ width: 120 }}
        />
        <Button startIcon={<RefreshIcon />} onClick={load} disabled={loading}>
          Refresh
        </Button>
      </Box>
      {error && (
        <Alert severity="error" sx={{ mb: 1 }}>
          {error}
        </Alert>
      )}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>ID</TableCell>
                  <TableCell>User</TableCell>
                  <TableCell>Level</TableCell>
                  <TableCell>Trophies</TableCell>
                  <TableCell>Open cycle</TableCell>
                  <TableCell>Updated</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center">
                      No profiles found.
                    </TableCell>
                  </TableRow>
                ) : (
                  rows.map((row) => {
                    const oc = row.open_cycle;
                    return (
                      <TableRow
                        key={row.id ?? row.profile_id}
                        hover
                        sx={{ cursor: 'pointer' }}
                        onClick={() => openDetail(row.id ?? row.profile_id)}
                      >
                        <TableCell>{row.id ?? row.profile_id}</TableCell>
                        <TableCell>
                          {row.username || row.user_id || row.guest_hash_prefix || '—'}
                        </TableCell>
                        <TableCell>{formatNullable(row.current_level ?? row.level)}</TableCell>
                        <TableCell>{row.total_trophies ?? row.trophies ?? 0}</TableCell>
                        <TableCell>
                          {oc
                            ? `${oc.games_played ?? 0}/${oc.games_required ?? '—'} (W:${oc.wins ?? oc.games_won ?? 0})`
                            : '—'}
                        </TableCell>
                        <TableCell>{formatDateTime(row.updated_at || row.last_played_at)}</TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <PaginationBar
            offset={offset}
            total={total}
            rowCount={rows.length}
            loading={loading}
            onPrev={() => setOffset((o) => Math.max(0, o - PAGE_SIZE))}
            onNext={() => setOffset((o) => o + PAGE_SIZE)}
          />
        </>
      )}

      <Dialog open={detailLoading || !!detail} onClose={() => setDetail(null)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          Profile detail
          <IconButton onClick={() => setDetail(null)} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {detailLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
            <JsonPreview data={detail} />
          )}
        </DialogContent>
      </Dialog>
    </Box>
  );
}

function CyclesPanel({ fromDate, toDate, onSessionExpired, showSnackbar }) {
  const [status, setStatus] = useState('');
  const [level, setLevel] = useState('');
  const [outcome, setOutcome] = useState('');
  const [profileId, setProfileId] = useState('');
  const [offset, setOffset] = useState(0);
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await listLeagueCycles({
        status: status || undefined,
        level: level || undefined,
        outcome: outcome || undefined,
        profile_id: profileId || undefined,
        from: fromDate || undefined,
        to: toDate || undefined,
        limit: PAGE_SIZE,
        offset,
      });
      if (onSessionExpired(res)) return;
      if (!isMindCricketApiSuccess(res)) {
        setError(res?.message || 'Failed to load cycles.');
        setRows([]);
        setTotal(0);
        return;
      }
      const data = getMindCricketApiPayload(res) || {};
      setRows(data.rows || []);
      setTotal(data.total ?? 0);
    } catch (err) {
      setError(err.message || 'Failed to load cycles.');
    } finally {
      setLoading(false);
    }
  }, [fromDate, toDate, level, offset, onSessionExpired, outcome, profileId, status]);

  useEffect(() => {
    load();
  }, [load]);

  const openDetail = async (id) => {
    setDetailLoading(true);
    setDetail(null);
    try {
      const res = await getLeagueCycle(id);
      if (onSessionExpired(res)) return;
      if (!isMindCricketApiSuccess(res)) {
        showSnackbar(res?.message || 'Failed to load cycle.', 'error');
        return;
      }
      setDetail(getMindCricketApiPayload(res));
    } catch (err) {
      showSnackbar(err.message || 'Failed to load cycle.', 'error');
    } finally {
      setDetailLoading(false);
    }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, mb: 2 }}>
        <TextField
          select
          size="small"
          label="Status"
          value={status}
          onChange={(e) => {
            setStatus(e.target.value);
            setOffset(0);
          }}
          sx={{ minWidth: 130 }}
        >
          <MenuItem value="">Any</MenuItem>
          <MenuItem value="open">Open</MenuItem>
          <MenuItem value="completed">Completed</MenuItem>
        </TextField>
        <TextField
          size="small"
          label="Level"
          type="number"
          value={level}
          onChange={(e) => {
            setLevel(e.target.value);
            setOffset(0);
          }}
          sx={{ width: 100 }}
        />
        <TextField
          select
          size="small"
          label="Outcome"
          value={outcome}
          onChange={(e) => {
            setOutcome(e.target.value);
            setOffset(0);
          }}
          sx={{ minWidth: 140 }}
        >
          <MenuItem value="">Any</MenuItem>
          {CYCLE_OUTCOMES.map((o) => (
            <MenuItem key={o} value={o}>
              {o}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          size="small"
          label="Profile ID"
          value={profileId}
          onChange={(e) => {
            setProfileId(e.target.value);
            setOffset(0);
          }}
          sx={{ width: 120 }}
        />
        <Button startIcon={<RefreshIcon />} onClick={load} disabled={loading}>
          Refresh
        </Button>
      </Box>
      {error && (
        <Alert severity="error" sx={{ mb: 1 }}>
          {error}
        </Alert>
      )}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Cycle</TableCell>
                  <TableCell>Profile</TableCell>
                  <TableCell>Level</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Progress</TableCell>
                  <TableCell>Outcome</TableCell>
                  <TableCell>Started</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} align="center">
                      No cycles found.
                    </TableCell>
                  </TableRow>
                ) : (
                  rows.map((row) => (
                    <TableRow
                      key={row.id ?? row.cycle_id}
                      hover
                      sx={{ cursor: 'pointer' }}
                      onClick={() => openDetail(row.id ?? row.cycle_id)}
                    >
                      <TableCell>{row.id ?? row.cycle_id}</TableCell>
                      <TableCell>{formatNullable(row.profile_id)}</TableCell>
                      <TableCell>{formatNullable(row.level)}</TableCell>
                      <TableCell>{formatNullable(row.status)}</TableCell>
                      <TableCell>
                        {row.games_played ?? 0}/{row.games_required ?? '—'} (W:
                        {row.wins ?? row.games_won ?? 0})
                      </TableCell>
                      <TableCell>{formatNullable(row.outcome)}</TableCell>
                      <TableCell>{formatDateTime(row.started_at || row.created_at)}</TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <PaginationBar
            offset={offset}
            total={total}
            rowCount={rows.length}
            loading={loading}
            onPrev={() => setOffset((o) => Math.max(0, o - PAGE_SIZE))}
            onNext={() => setOffset((o) => o + PAGE_SIZE)}
          />
        </>
      )}

      <Dialog open={detailLoading || !!detail} onClose={() => setDetail(null)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          Cycle detail
          <IconButton onClick={() => setDetail(null)} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {detailLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
            <JsonPreview data={detail} />
          )}
        </DialogContent>
      </Dialog>
    </Box>
  );
}

function LeagueMatchesPanel({ fromDate, toDate, onSessionExpired }) {
  const [cycleId, setCycleId] = useState('');
  const [won, setWon] = useState('');
  const [level, setLevel] = useState('');
  const [offset, setOffset] = useState(0);
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await listLeagueMatches({
        cycle_id: cycleId || undefined,
        won: won === '' ? undefined : won,
        level: level || undefined,
        from: fromDate || undefined,
        to: toDate || undefined,
        limit: PAGE_SIZE,
        offset,
      });
      if (onSessionExpired(res)) return;
      if (!isMindCricketApiSuccess(res)) {
        setError(res?.message || 'Failed to load league matches.');
        setRows([]);
        setTotal(0);
        return;
      }
      const data = getMindCricketApiPayload(res) || {};
      setRows(data.rows || []);
      setTotal(data.total ?? 0);
    } catch (err) {
      setError(err.message || 'Failed to load league matches.');
    } finally {
      setLoading(false);
    }
  }, [cycleId, fromDate, level, offset, onSessionExpired, toDate, won]);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <Box>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, mb: 2 }}>
        <TextField
          size="small"
          label="Cycle ID"
          value={cycleId}
          onChange={(e) => {
            setCycleId(e.target.value);
            setOffset(0);
          }}
          sx={{ width: 120 }}
        />
        <TextField
          select
          size="small"
          label="Won"
          value={won}
          onChange={(e) => {
            setWon(e.target.value);
            setOffset(0);
          }}
          sx={{ minWidth: 120 }}
        >
          <MenuItem value="">Any</MenuItem>
          <MenuItem value="true">Won</MenuItem>
          <MenuItem value="false">Lost</MenuItem>
        </TextField>
        <TextField
          size="small"
          label="Level"
          type="number"
          value={level}
          onChange={(e) => {
            setLevel(e.target.value);
            setOffset(0);
          }}
          sx={{ width: 100 }}
        />
        <Button startIcon={<RefreshIcon />} onClick={load} disabled={loading}>
          Refresh
        </Button>
      </Box>
      {error && (
        <Alert severity="error" sx={{ mb: 1 }}>
          {error}
        </Alert>
      )}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Match</TableCell>
                  <TableCell>Cycle</TableCell>
                  <TableCell>Level</TableCell>
                  <TableCell>Won</TableCell>
                  <TableCell>Created</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5} align="center">
                      No league matches found.
                    </TableCell>
                  </TableRow>
                ) : (
                  rows.map((row, idx) => (
                    <TableRow key={row.id ?? row.match_id ?? idx}>
                      <TableCell>{row.match_id ?? row.id}</TableCell>
                      <TableCell>{formatNullable(row.cycle_id)}</TableCell>
                      <TableCell>{formatNullable(row.level)}</TableCell>
                      <TableCell>
                        {row.won == null ? '—' : row.won ? 'Won' : 'Lost'}
                      </TableCell>
                      <TableCell>{formatDateTime(row.created_at)}</TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <PaginationBar
            offset={offset}
            total={total}
            rowCount={rows.length}
            loading={loading}
            onPrev={() => setOffset((o) => Math.max(0, o - PAGE_SIZE))}
            onNext={() => setOffset((o) => o + PAGE_SIZE)}
          />
        </>
      )}
    </Box>
  );
}

function EventsPanel({ fromDate, toDate, onSessionExpired }) {
  const [eventType, setEventType] = useState('');
  const [userId, setUserId] = useState('');
  const [offset, setOffset] = useState(0);
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await listLeagueEvents({
        event_type: eventType || undefined,
        user_id: userId || undefined,
        from: fromDate || undefined,
        to: toDate || undefined,
        limit: PAGE_SIZE,
        offset,
      });
      if (onSessionExpired(res)) return;
      if (!isMindCricketApiSuccess(res)) {
        setError(res?.message || 'Failed to load events.');
        setRows([]);
        setTotal(0);
        return;
      }
      const data = getMindCricketApiPayload(res) || {};
      setRows(data.rows || []);
      setTotal(data.total ?? 0);
    } catch (err) {
      setError(err.message || 'Failed to load events.');
    } finally {
      setLoading(false);
    }
  }, [eventType, fromDate, offset, onSessionExpired, toDate, userId]);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <Box>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, mb: 2 }}>
        <TextField
          select
          size="small"
          label="Event type"
          value={eventType}
          onChange={(e) => {
            setEventType(e.target.value);
            setOffset(0);
          }}
          sx={{ minWidth: 280 }}
        >
          <MenuItem value="">Any</MenuItem>
          {LEAGUE_EVENT_TYPES.map((t) => (
            <MenuItem key={t} value={t}>
              {t.replace('super_over_league_', '')}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          size="small"
          label="User ID"
          value={userId}
          onChange={(e) => {
            setUserId(e.target.value);
            setOffset(0);
          }}
          sx={{ width: 120 }}
        />
        <Button startIcon={<RefreshIcon />} onClick={load} disabled={loading}>
          Refresh
        </Button>
      </Box>
      {error && (
        <Alert severity="error" sx={{ mb: 1 }}>
          {error}
        </Alert>
      )}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Event</TableCell>
                  <TableCell>User</TableCell>
                  <TableCell>Payload</TableCell>
                  <TableCell>Created</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={4} align="center">
                      No events found.
                    </TableCell>
                  </TableRow>
                ) : (
                  rows.map((row, idx) => (
                    <TableRow key={row.id ?? idx}>
                      <TableCell>{row.event_type}</TableCell>
                      <TableCell>{formatNullable(row.user_id)}</TableCell>
                      <TableCell sx={{ maxWidth: 360 }}>
                        <Typography variant="caption" component="span" sx={{ wordBreak: 'break-all' }}>
                          {typeof row.payload === 'object'
                            ? JSON.stringify(row.payload)
                            : formatNullable(row.payload ?? row.metadata)}
                        </Typography>
                      </TableCell>
                      <TableCell>{formatDateTime(row.created_at)}</TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <PaginationBar
            offset={offset}
            total={total}
            rowCount={rows.length}
            loading={loading}
            onPrev={() => setOffset((o) => Math.max(0, o - PAGE_SIZE))}
            onNext={() => setOffset((o) => o + PAGE_SIZE)}
          />
        </>
      )}
    </Box>
  );
}

function AnalyticsTab({ onSessionExpired, showSnackbar }) {
  const today = getLocalDateString();
  const [fromDate, setFromDate] = useState(addDaysToDateString(today, -30));
  const [toDate, setToDate] = useState(today);
  const [section, setSection] = useState(0);

  return (
    <Box>
      <Paper sx={{ p: 2, mb: 2, display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center' }}>
        <TextField
          size="small"
          label="From"
          type="date"
          value={fromDate}
          onChange={(e) => setFromDate(e.target.value)}
          InputLabelProps={{ shrink: true }}
        />
        <TextField
          size="small"
          label="To"
          type="date"
          value={toDate}
          onChange={(e) => setToDate(e.target.value)}
          InputLabelProps={{ shrink: true }}
        />
        <Typography variant="caption" color="text.secondary">
          Date range applies to overview, matches, users, and league deep-dive lists.
        </Typography>
      </Paper>

      <Tabs value={section} onChange={(_, v) => setSection(v)} sx={{ mb: 2 }}>
        <Tab label="Overview" />
        <Tab label="Matches" />
        <Tab label="Users" />
        <Tab label="League deep-dive" />
      </Tabs>

      {section === 0 && (
        <OverviewSection fromDate={fromDate} toDate={toDate} onSessionExpired={onSessionExpired} />
      )}
      {section === 1 && (
        <MatchesSection
          fromDate={fromDate}
          toDate={toDate}
          onSessionExpired={onSessionExpired}
          showSnackbar={showSnackbar}
        />
      )}
      {section === 2 && (
        <UsersSection fromDate={fromDate} toDate={toDate} onSessionExpired={onSessionExpired} />
      )}
      {section === 3 && (
        <LeagueDeepDive
          fromDate={fromDate}
          toDate={toDate}
          onSessionExpired={onSessionExpired}
          showSnackbar={showSnackbar}
        />
      )}
    </Box>
  );
}

export default AnalyticsTab;
